import { createTheme, type Theme } from '@mui/material/styles';

import { tokens } from './tokens';

/**
 * Custom palette color for the middle defect-severity tier (AUT-151).
 * `minor` maps to `palette.warning`, `critical` to `palette.error`; the
 * `medium` tier sits between them and has no stock MUI slot, so we add a
 * dedicated `severityMedium` color (orange) rather than reaching for a
 * raw hex at the call-site (CLAUDE.md rule #1 / no magic literals).
 */
declare module '@mui/material/styles' {
  interface Palette {
    severityMedium: Palette['warning'];
  }
  interface PaletteOptions {
    severityMedium?: PaletteOptions['warning'];
  }
}

// Opt the custom color into `<Chip color="severityMedium" />`.
declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    severityMedium: true;
  }
}

/**
 * MUI v7 theme derived from `tokens.ts` (mockup parity per LOCK 10/11).
 *
 * `spacing: 4` makes `theme.spacing(1) === 4px` so call-sites can use
 * MUI's spacing helper while staying on the mockup grid (`var(--space-4)`
 * = 16 = `theme.spacing(4)`).
 *
 * v1 ships LIGHT THEME ONLY (LOCK 10). Dark theme is deferred to a
 * future ticket — keep the createTheme call light-only for now.
 */
export const theme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.brand.primary,
      dark: tokens.brand.primaryHover,
      contrastText: tokens.brand.onPrimary,
    },
    background: {
      default: tokens.surface.canvas,
      paper: tokens.surface.base,
    },
    text: {
      primary: tokens.text.primary,
      secondary: tokens.text.secondary,
      disabled: tokens.text.disabled,
    },
    success: { main: tokens.state.success },
    warning: { main: tokens.state.warning },
    error: { main: tokens.state.danger },
    info: { main: tokens.state.info },
    severityMedium: {
      main: tokens.state.warningStrong,
      light: tokens.state.warningStrongBg,
      contrastText: tokens.state.warningStrongText,
    },
    divider: tokens.border.default,
  },
  typography: {
    fontFamily: tokens.font.family,
    fontSize: tokens.type.body,
    htmlFontSize: 16,
    h1: { fontSize: tokens.type.h1, lineHeight: `${tokens.line.h1}px`, fontWeight: 700 },
    h2: { fontSize: tokens.type.h2, lineHeight: `${tokens.line.h2}px`, fontWeight: 600 },
    h3: { fontSize: tokens.type.h3, lineHeight: `${tokens.line.h3}px`, fontWeight: 600 },
    body1: { fontSize: tokens.type.body, lineHeight: `${tokens.line.body}px` },
    body2: { fontSize: tokens.type.bodySm, lineHeight: `${tokens.line.bodySm}px` },
    caption: {
      fontSize: tokens.type.caption,
      lineHeight: `${tokens.line.caption}px`,
      color: tokens.text.secondary,
    },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  spacing: tokens.spacing.base,
  shape: { borderRadius: tokens.radius.button },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: tokens.radius.button },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
  },
});
